import EastIcon from "@mui/icons-material/East";
import React, { useState, useEffect } from "react";
import OrderImg from "../../../../assets/images/order-banner-image.png";
import PropTypes from "prop-types";
import config from "config/config";

const TimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M11.9994 9.5V13.5L14.4994 15M11.9994 5C7.30497 5 3.49939 8.80558 3.49939 13.5C3.49939 18.1944 7.30497 22 11.9994 22C16.6938 22 20.4994 18.1944 20.4994 13.5C20.4994 8.80558 16.6938 5 11.9994 5ZM11.9994 5V2M9.99939 2H13.9994M20.3284 5.59204L18.8284 4.09204L19.5784 4.84204M3.67041 5.59204L5.17041 4.09204L4.42041 4.84204"
      stroke="#FFCC00"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const centsToDollars = (cents) => (Number(cents || 0) / 100).toFixed(2);

const OrderNowBanner = ({ campaign, restaurantDetails, expiryDate }) => {
  const calculateTimeLeft = () => {
    if (!expiryDate) return { days: 0, hours: 0, minutes: 0, hasExpired: true };
    const expiryDateTime = new Date(expiryDate);
    const diff = expiryDateTime - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, hasExpired: true };
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      hasExpired: false,
    };
  };

  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());
  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 60000);
    return () => clearInterval(timer);
  }, [expiryDate]);

  if (timeLeft?.hasExpired) return null;

  const hasOrderNowUrl =
    restaurantDetails?.orderNowUrl && restaurantDetails.orderNowUrl.trim() !== "";

  const minValue = campaign?.minimumOrderValueInCents;
  const isOverride = !!campaign?.override;
  const eligibleCount = Number(campaign?.eligibleOrdersCount);
  const hasCap = Number.isFinite(eligibleCount) && eligibleCount > 0;

  // --- Campaign Details Rendering (uses validDetails) ---
  const renderCampaignDetails = (details) => {
    if (!details || details.length === 0) return null;

    const allOrdersDetail = details.find((d) => d.rewardCondition === "ALL_ORDERS");
    const firstOrderDetail = details.find((d) => d.rewardCondition === "FIRST_ORDER");

    const buildHeadline = (detail, isPrimary) => {
      const { rewardType, fixedCashback, multiplierCashback, rewardCondition } = detail;
      const condText = rewardCondition === "FIRST_ORDER" ? "on your FIRST order" : "on EVERY order";

      if (isOverride) {
        // ONLY campaign points — no cashback wording
        if (rewardType === "FIXED") {
          return `Earn ${fixedCashback} cashback points ($${centsToDollars(
            fixedCashback
          )}) ${condText}`;
        } else {
          return `Earn ${multiplierCashback}X cashback points ${condText}`;
        }
      } else {
        // Stacks with loyalty rewards
        if (rewardType === "FIXED") {
          return `Enjoy an additional reward of ${fixedCashback} cashback points ($${centsToDollars(
            fixedCashback
          )}) ${condText}, on top of your existing cashback incentive,`;
        } else {
          return `Enjoy an additional +${multiplierCashback}X cashback ${condText}, on top of your existing cashback incentive,`;
        }
      }
    };

    return (
      <div className="mb-3">
        {allOrdersDetail && (
          <h2 className="text-xl font-semibold text-left">
            {buildHeadline(allOrdersDetail, true)} from{" "}
            <span
              onClick={() => {
                window.open(
                  `${
                    config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"]
                      .API_LANDING_PAGE_BASE_URL
                  }/restaurants/${restaurantDetails?.resaturantPrefix}`,
                  "_blank"
                );
              }}
              className="hover:underline cursor-pointer"
            >
              {restaurantDetails?.restaurantName}
            </span>
          </h2>
        )}

        {allOrdersDetail && firstOrderDetail && (
          <p className="text-base md:text-lg mt-2 text-left font-medium">
            <span
              className="inline-block text-sm font-semibold px-3 py-1 rounded-full mr-2"
              style={{ backgroundColor: "#FFF5EA", color: "#B45309" }}
            >
              LIMITED TIME OFFER
            </span>
            {buildHeadline(firstOrderDetail, false)}
          </p>
        )}

        {!allOrdersDetail && firstOrderDetail && (
          <h2 className="text-xl font-semibold text-left">
            {buildHeadline(firstOrderDetail, true)} from{" "}
            <span
              onClick={() => {
                window.open(
                  `${
                    config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"]
                      .API_LANDING_PAGE_BASE_URL
                  }/restaurants/${restaurantDetails?.resaturantPrefix}`,
                  "_blank"
                );
              }}
              className="hover:underline cursor-pointer"
            >
              {restaurantDetails?.restaurantName}
            </span>
          </h2>
        )}

        {minValue !== "0" && (
          <p className="text-base md:text-base text-white mt-1 text-left">
            Minimum order value: ${centsToDollars(minValue)}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="w-full bg-[#238856] text-white rounded-[16px] overflow-hidden mb-4 -mt-4">
      <div className="container mx-auto p-[15px] lg:py-0 lg:px-6 lg:pt-2 flex flex-col md:flex-row justify-between">
        {/* Left Content */}
        <div
          style={{ marginTop: !hasOrderNowUrl && 22 }}
          className="flex-1 mt-1 text-center md:text-left mb-4 md:mb-0"
        >
          {renderCampaignDetails(campaign?.validDetails)}

          {hasCap && (
            <p className="text-sm opacity-90 text-left mt-1 mb-5">
              Applies to your next {eligibleCount} order{eligibleCount > 1 ? "s" : ""}.
            </p>
          )}

          {hasOrderNowUrl && (
            <button
              onClick={() => window.open(restaurantDetails.orderNowUrl, "_blank")}
              className="items-center bg-white text-emerald-800 px-4 py-2 mb-1 rounded-md font-medium text-sm hover:bg-emerald-50 transition-colors mt-3 hidden md:inline-flex"
            >
              Order now <EastIcon className="ml-2 h-4 w-4 " />
            </button>
          )}
        </div>

        {/* Right Content (Image + Countdown) */}
        <div className="flex gap-10 flex-row items-center justify-between md:justify-start">
          <div className="hidden lg:block w-32 md:w-[208px] h-[150px] mb-4 md:mb-0">
            <img src={OrderImg} alt="Person holding food items" className="h-full w-full" />
          </div>

          {hasOrderNowUrl && (
            <button
              onClick={() => window.open(restaurantDetails.orderNowUrl, "_blank")}
              className="items-center bg-white text-emerald-800 px-4 py-2 rounded-md font-medium text-sm hover:bg-emerald-50 transition-colors mt-3 md:hidden inline-flex"
            >
              Order now <EastIcon className="ml-2 h-4 w-4" />
            </button>
          )}

          {expiryDate && (
            <div className="text-center">
              <p className="text-base uppercase md:-mt-2 md:mb-2 text-white md:block hidden">
                OFFER EXPIRES IN
              </p>
              <div className="flex items-center mt-1 md:mt-0 md:items-start md:space-x-2">
                <div className="md:hidden">
                  <TimeIcon />
                </div>
                {["days", "hours", "minutes"].map((unit) => (
                  <div
                    key={unit}
                    className="md:bg-white md:text-black text-center rounded p-2 md:w-14 flex flex-col items-center"
                  >
                    <span className="md:text-2xl text-base font-bold md:font-normal">
                      {timeLeft?.[unit]}
                    </span>
                    <span className="text-[9px] md:text-xs uppercase ">{unit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

OrderNowBanner.propTypes = {
  campaign: PropTypes.shape({
    override: PropTypes.bool,
    minimumOrderValueInCents: PropTypes.string,
    eligibleOrdersCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    validDetails: PropTypes.arrayOf(
      PropTypes.shape({
        rewardType: PropTypes.string,
        rewardCondition: PropTypes.string,
        fixedCashback: PropTypes.number,
        multiplierCashback: PropTypes.number,
      })
    ),
  }),
  restaurantDetails: PropTypes.any,
  expiryDate: PropTypes.string,
};

export default OrderNowBanner;
